import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X } from 'lucide-react';

interface CommissionRule {
  id: string;
  name: string;
  min_value: number;
  max_value: number | null;
  percentage: number;
  active: boolean;
}

export default function ConfiguracaoComissoes() {
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    min_value: '',
    max_value: '',
    percentage: ''
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setRules(data.data);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        min_value: parseFloat(formData.min_value),
        max_value: formData.max_value ? parseFloat(formData.max_value) : null,
        percentage: parseFloat(formData.percentage)
      };

      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setRules([...rules, data.data]);
        setFormData({ name: '', min_value: '', max_value: '', percentage: '' });
        setShowAddForm(false);
        alert('Regra criada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao criar regra:', error);
      alert('Erro ao criar regra');
    }
  };

  const handleUpdate = async (id: string, updates: Partial<CommissionRule>) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/rules/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await res.json();
      if (data.success) {
        setRules(rules.map(r => r.id === id ? data.data : r));
        alert('Regra atualizada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao atualizar regra:', error);
      alert('Erro ao atualizar regra');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar esta regra?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/rules/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success) {
        setRules(rules.filter(r => r.id !== id));
        alert('Regra deletada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      alert('Erro ao deletar regra');
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    await handleUpdate(id, { active: !active });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuração de Comissões</h1>
          <p className="text-gray-600">Gerencie as regras de comissão por faixa de valor</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center gap-2"
        >
          {showAddForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          {showAddForm ? 'Cancelar' : 'Nova Regra'}
        </button>
      </div>

      {/* Formulário de Adicionar */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Nova Regra de Comissão</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Ex: Até $100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor Mínimo ($)</label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.min_value}
                onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor Máximo ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.max_value}
                onChange={(e) => setFormData({ ...formData, max_value: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Deixe vazio para ilimitado"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Percentual (%)</label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.percentage}
                onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="5.00"
              />
            </div>
            <div className="md:col-span-4">
              <button
                type="submit"
                className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 flex items-center gap-2"
              >
                <Save className="h-5 w-5" />
                Salvar Regra
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabela de Regras */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Regras Cadastradas</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Faixa de Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percentual</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    Nenhuma regra cadastrada
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {rule.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${rule.min_value.toFixed(2)} - {rule.max_value ? `$${rule.max_value.toFixed(2)}` : 'Ilimitado'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      {rule.percentage}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(rule.id, rule.active)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          rule.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {rule.active ? 'Ativa' : 'Inativa'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(rule.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Deletar"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Informações */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Como funciona</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• As regras são aplicadas automaticamente ao criar uma venda</li>
          <li>• O sistema busca a primeira regra que se encaixa no valor da venda</li>
          <li>• Apenas regras ativas são consideradas no cálculo</li>
          <li>• Deixe o valor máximo vazio para criar uma regra sem limite superior</li>
        </ul>
      </div>
    </div>
  );
}
